import { useRef } from 'react'
import { cn } from '@/lib/utils'
import { MarkdownText } from '@/components/MarkdownText'

interface AnswerOptionProps {
  optionKey: string    // "A", "B", "C", "D"
  text: string
  isSelected: boolean
  isCorrect: boolean
  isDisabled: boolean  // true after answer is confirmed
  revealAnswer: boolean
  onClick: (key: string) => void
  onNext?: () => void  // when locked, clicking any option advances to next question
  keyHint?: string     // keyboard shortcut hint, e.g. "1"
}

export function AnswerOption({
  optionKey,
  text,
  isSelected,
  isCorrect,
  isDisabled,
  revealAnswer,
  onClick,
  onNext,
  keyHint,
}: AnswerOptionProps) {
  // Prevent accidental selection when the user is scrolling over an option.
  // We track vertical movement: if the pointer/touch moves more than 8px
  // between pointerdown and click, we treat it as a scroll gesture and ignore it.
  const touchStartY = useRef<number | null>(null)
  const scrolling = useRef(false)

  const baseClasses =
    'w-full text-left px-4 py-3.5 min-h-[52px] rounded-xl text-base font-medium transition-colors flex items-start gap-3'

  const isNavigable = isDisabled && !!onNext

  const stateClasses = cn({
    // Pending selection (chosen but not yet confirmed) — clear highlight
    'bg-primary/15 dark:bg-primary/25 ring-2 ring-primary/40':
      !isDisabled && isSelected,

    // Unselected and available — hoverable
    'bg-muted/40 hover:bg-accent hover:text-accent-foreground cursor-pointer':
      !isDisabled && !isSelected,

    // Confirmed correct
    'bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-100':
      isDisabled && isSelected && isCorrect && revealAnswer,

    // Confirmed wrong
    'bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100':
      isDisabled && isSelected && !isCorrect && revealAnswer,

    // Confirmed, answer not yet revealed (mock exam / end-reveal)
    'bg-primary/15 dark:bg-primary/25':
      isDisabled && isSelected && !revealAnswer,

    // Not selected, but correct (revealed)
    'bg-green-50/50 text-green-800 dark:bg-green-950/50 dark:text-green-300':
      isDisabled && !isSelected && isCorrect && revealAnswer,

    // Not selected, disabled — muted
    'bg-muted/40 text-muted-foreground opacity-60':
      isDisabled && !isSelected && (!isCorrect || !revealAnswer),

    // Cursor: pointer when interactive
    'cursor-pointer': !isDisabled || isNavigable,
    'cursor-default': isDisabled && !isNavigable,

    // Subtle hover/active feedback when locked but clickable to navigate
    'hover:opacity-80 active:opacity-60': isNavigable,
  })

  function handlePointerDown(e: React.PointerEvent) {
    touchStartY.current = e.clientY
    scrolling.current = false
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (touchStartY.current !== null && Math.abs(e.clientY - touchStartY.current) > 8) {
      scrolling.current = true
    }
  }

  function handleClick() {
    if (scrolling.current) return
    if (isDisabled) {
      onNext?.()
      return
    }
    onClick(optionKey)
  }

  return (
    // div+role instead of button so block-level markdown (tables, paragraphs) is valid HTML
    <div
      role="button"
      tabIndex={isDisabled && !isNavigable ? -1 : 0}
      aria-disabled={isDisabled && !isNavigable}
      aria-label={`Option ${optionKey}`}
      data-sound="select"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onClick={handleClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          if (isDisabled) {
            onNext?.()
          } else {
            onClick(optionKey)
          }
        }
      }}
      className={cn(baseClasses, stateClasses)}
    >
      <span aria-hidden="true" className="flex-shrink-0 w-7 h-7 rounded-full border-2 border-current flex items-center justify-center text-sm font-bold">
        {optionKey}
      </span>
      <MarkdownText
        inline
        className="flex-1 pt-0.5 [&_p]:my-0 [&_table]:text-sm [&_th]:text-left [&_td]:pr-3"
      >
        {text}
      </MarkdownText>
      {keyHint && !isDisabled && (
        <kbd
          aria-hidden="true"
          className="hidden sm:flex self-start mt-1 shrink-0 h-5 min-w-[1.25rem] items-center justify-center rounded border border-border bg-muted/70 text-[10px] font-mono font-semibold text-muted-foreground/70 px-1"
        >
          {keyHint}
        </kbd>
      )}
    </div>
  )
}
